import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import useQueryParams from 'hooks/UseQueryParams';
import { errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CheckboxInput, CommonInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { CommonTextAreaInput } from 'sharedComponents/inputGroup/CommonTextAreaInput';
import { postTradeInLeadCreation } from 'store/TradeInState';
import { RootState } from 'store/store';

const Step1: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();

  const hasCalledNextStep = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();
  const { authenticateClientData } = useSelector((state: RootState) => state.auth);
  const { vehicleOptions } = useSelector((state: RootState) => state.tradeInStore);
  const { offerValue } = useSelector((state: RootState) => state.commonModuleStore);

  const leadSourceId = authenticateClientData?.data?.widgetData?.leadSourceId;
  const year = queryParams.get('year');
  const make = queryParams.get('make');
  const model = queryParams.get('model');
  const trim = queryParams.get('trim');
  const heading = queryParams.get('heading');
  const zipCode = authenticateClientData.data?.widgetData?.zipCode;
  const dealershipName = authenticateClientData.data?.widgetData?.dealershipName;
  const { control, setValue, trigger, getValues } = useFormContext();

  useEffect(() => {
    setTimeout(() => {
      const vehicleSearch = getValues('vehicleSearch') || {};

      setValue('year', year || vehicleSearch.year || '');
      setValue('make', make || vehicleSearch.make || '');
      setValue('model', model || vehicleSearch.model || '');
      setValue('trim', trim || vehicleSearch.trim || '');
      setValue('heading', heading || vehicleSearch.heading || '');
    });
  }, [year, make, model, trim, heading, setValue, getValues]);

  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { firstName, lastName, phone, email, mileage, year, model, make, trim, province } = getValues();

    const updatedData = {
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      email: email,
      leadSourceId: leadSourceId,
      country: 'CA',
      birthDate: '1991-01-01',
      zipCode: zipCode,
      leadDetails: {
        Odometer: mileage,
        'Car Year': year,
        'Car Model': model,
        'Car Make': make,
        'Car Trim': trim,
        'Vehicle Status': 'Used',
        'Offer Value': offerValue?.Value || offerValue?.ResponseMessage || 'Unavailable',
      },
      province: province,
    };

    dispatch(
      postTradeInLeadCreation({
        data: updatedData,
        leadTypeID: authenticateClientData?.data?.widgetData?.leadTypeId,
      })
    ).then((res) => {
      if (Object.keys(res?.payload).length && res?.payload?.message) {
        // const formData = {
        //   email: email,
        //   phone: `+${phone}`,
        //   firstName: firstName,
        //   lastName: lastName,
        //   region: province,
        //   country: 'CA',
        // };
        // sendWindowDataLayer(formData); termporarily commented out
        onNext?.();
      }
    });
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <p className='text-center'>{t('checkAvailability.checkAvail')}</p>
      <div className='two-col'>
        <div className='form-group'>
          <CommonInput
            name='firstName'
            control={control}
            label={t('stepperForm.firstName')}
            placeholder={t('stepperForm.enterFirstName')}
            rules={formRequiredRules(errorMessage(t).firstNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonInput
            name='lastName'
            control={control}
            label={t('stepperForm.lastName')}
            placeholder={t('stepperForm.enterLastName')}
            rules={formRequiredRules(errorMessage(t).lastNameRequired)}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonPhoneInput
            name='phone'
            control={control}
            label={t('stepperForm.mobileNumber')}
            placeholder={t('stepperForm.enterNumber')}
            rules={{
              ...formRequiredRules(errorMessage(t).phoneRequired),
              ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
        <div className='form-group'>
          <CommonInput
            name='email'
            control={control}
            hint={t('stepperForm.emailNote')}
            label={t('stepperForm.emailEg')}
            placeholder={t('stepperForm.enterEmail')}
            rules={{
              ...formRequiredRules(errorMessage(t).emailRequired),
              ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
            }}
            triggerValidationForField={trigger}
          />
        </div>
      </div>
      <div className='form-group'>
        <CommonTextAreaInput
          name='message'
          control={control}
          label={t('checkAvailability.yourMessage')}
          rules={{
            ...formRequiredRules(errorMessage(t).required),
          }}
          triggerValidationForField={trigger}
        />
      </div>
      <div className='form-group'>
        <CheckboxInput
          name='termsAndCondition'
          control={control}
          label={t('checkAvailability.checkBoxNote', { dealershipName })}
          rules={formRequiredRules(errorMessage(t).termsAndConditionRequired)}
          triggerValidationForField={trigger}
        />
      </div>
    </>
  );
};

const StepperForm1 = React.forwardRef(Step1);

export default StepperForm1;
